import type { ChangeEvent } from 'react';

import type { CountryData, CountryGuessResult, ParsedCountry } from '@/components/ui/input';

import {
  defaultCountries,
  type ApplyMaskProps,
  type FormatPhoneConfig,
  type GetActiveFormattingMaskProps,
  type GetCountryProps,
  type GetCursorPositionProps,
  type GuessCountryByPartialNumberProps,
  type HandlePhoneChangeProps,
  type HandlePhoneChangeReturn,
  type HandleUserInputOptions,
  type HandleUserInputReturn,
  type UsePhoneInputConfig,
  type IPhoneNumberManager,
} from '.';

export class PhoneNumberManager implements IPhoneNumberManager {
  private defaultHistoryConfig = {
    size: 20,
    overrideLastItemDebounceMS: -1, // overriding is disabled by default
  };
  private defaultConfig: Required<
    Omit<UsePhoneInputConfig, 'onChange' | 'inputRef'> // omit props with no default value
  > = {
    defaultCountry: 'pl',
    value: '',
    prefix: '+',
    defaultMask: '............', // 12 chars
    charAfterDialCode: ' ',
    historySaveDebounceMS: 200,
    disableCountryGuess: false,
    disableDialCodePrefill: false,
    forceDialCode: false,
    disableDialCodeAndPrefix: false,
    disableFormatting: false,
    countries: defaultCountries,
    preferredCountries: [],
  };
  private MASK_CHAR: string = '.';

  get getDefaultConfig(): Required<Omit<UsePhoneInputConfig, 'onChange' | 'inputRef'>> {
    return this.defaultConfig;
  }
  get getDefaultHistoryConfig(): {
    size: number;
    overrideLastItemDebounceMS: number;
  } {
    return this.defaultHistoryConfig;
  }

  // private methods

  private isNumeric(str?: string): boolean {
    if (!str) return false;
    return /^\d+$/.test(str);
  }
  private toE164(phone: string, prefix: string): string {
    return phone ? `${prefix}${this.removeNonDigits(phone)}` : '';
  }
  private removeNonDigits(value: string): string {
    return value.replace(/\D/g, '');
  }
  private getActiveFormattingMask(getActiveFormattingMaskProps: GetActiveFormattingMaskProps): string {
    const {
      phone,
      country,
      defaultMask = '............', // 12 chars
      disableFormatting = false,
    } = getActiveFormattingMaskProps;

    const format = country.format;

    const handleReturn = (mask: string): string => {
      return disableFormatting
        ? mask.replace(new RegExp(`[^${this.MASK_CHAR}]`, 'g'), '') // remove non MASK_CHAR symbols
        : mask;
    };

    if (!format) {
      return handleReturn(defaultMask);
    }

    if (typeof format === 'string') {
      return handleReturn(format);
    }

    if (!format['default']) {
      console.error(`[react-international-phone]: default mask for ${country.iso2} is not provided`);
      return handleReturn(defaultMask);
    }

    // format is object value -> parse key regex and get format string
    const matchedFormatKey: string | undefined = Object.keys(format).find((key) => {
      if (key === 'default') return false; // ignore default key

      const isValidRegex: boolean = key.charAt(0) === '/' && key.charAt(key.length - 1) === '/';

      if (!isValidRegex) {
        console.error(`[react-international-phone]: format regex "${key}" for ${country.iso2} is not valid`);
        return false;
      }

      // trim first and last symbols (to pass raw regex value to constructor)
      const regex = new RegExp(key.substring(1, key.length - 1));
      const valueWithoutDialCode = phone.replace(country.dialCode, '');

      return regex.test(this.removeNonDigits(valueWithoutDialCode));
    });

    return handleReturn((matchedFormatKey ? format[matchedFormatKey] : format.default) ?? defaultMask);
  }
  private getCursorPosition(getCursorPositionProps: GetCursorPositionProps): number {
    const { phoneBeforeInput, phoneAfterInput, phoneAfterFormatted, cursorPositionAfterInput, leftOffset = 0, deletion } = getCursorPositionProps;

    if (cursorPositionAfterInput < leftOffset) {
      return leftOffset;
    }

    if (!phoneBeforeInput) {
      return phoneAfterFormatted.length;
    }

    let afterInputPointIndex: number | null = null;

    // iterate from right to left and get first digit char
    for (let index: number = cursorPositionAfterInput - 1; index >= 0; index -= 1) {
      if (this.isNumeric(phoneAfterInput[index])) {
        afterInputPointIndex = index;
        break;
      }
    }

    if (afterInputPointIndex === null) {
      for (let index: number = 0; index < phoneAfterInput.length; index += 1) {
        if (this.isNumeric(phoneAfterFormatted[index])) {
          return index;
        }
      }

      return phoneAfterInput.length;
    }

    // find "digit index" of new char (only digits count)
    let digitIndex: number = 0;
    for (let index: number = 0; index < afterInputPointIndex; index += 1) {
      if (this.isNumeric(phoneAfterInput[index])) {
        digitIndex += 1;
      }
    }

    // find cursor position by going over digits until we get digitIndex
    let cursorPosition: number = 0;
    let digitsCounter: number = 0;
    for (let index: number = 0; index < phoneAfterFormatted.length; index += 1) {
      cursorPosition += 1;

      if (this.isNumeric(phoneAfterFormatted[index])) {
        digitsCounter += 1;
      }

      if (digitsCounter >= digitIndex + 1) {
        break;
      }
    }

    // set cursor before next digit (jump over mask chars on the right side)
    if (deletion !== 'backward') {
      while (!this.isNumeric(phoneAfterFormatted[cursorPosition]) && cursorPosition < phoneAfterFormatted.length) {
        cursorPosition += 1;
      }
    }

    return cursorPosition;
  }
  private guessCountryByPartialNumber(guessCountryByPartialNumberProps: GuessCountryByPartialNumberProps): CountryGuessResult {
    const { phone: partialPhone, countries = defaultCountries, currentCountryIso2 } = guessCountryByPartialNumberProps;

    const emptyResult = {
      country: undefined,
      fullDialCodeMatch: false,
    };
    if (!partialPhone) {
      return emptyResult;
    }

    const phone: string = this.removeNonDigits(partialPhone);

    if (!phone) {
      return emptyResult;
    }

    let result: CountryGuessResult = emptyResult;

    const updateResult = ({ country, fullDialCodeMatch }: { country: ParsedCountry; fullDialCodeMatch: boolean }) => {
      const sameDialCode: boolean = country.dialCode === result.country?.dialCode;
      const newPriorityValueLower: boolean = (country.priority ?? 0) < (result.country?.priority ?? 0);

      if (!sameDialCode || newPriorityValueLower) {
        result = { country, fullDialCodeMatch };
      }
    };

    for (const c of countries) {
      const parsedCountry = this.parseCountry(c);
      const { dialCode, areaCodes } = parsedCountry;

      // full match with dialCode
      if (phone.startsWith(dialCode)) {
        // make sure that we found the largest full dialCode
        const isNewDialCodeLonger: boolean = result.country ? Number(dialCode) >= Number(result.country.dialCode) : true;

        if (areaCodes) {
          const phoneWithoutDialCode: string = phone.substring(dialCode.length);
          for (const areaCode of areaCodes) {
            if (phoneWithoutDialCode.startsWith(areaCode)) {
              // found full match with area code
              return {
                country: parsedCountry,
                fullDialCodeMatch: true,
              };
            }
          }
        }

        if (isNewDialCodeLonger || dialCode === phone || !result.fullDialCodeMatch) {
          updateResult({
            country: parsedCountry,
            fullDialCodeMatch: true,
          });
        }
      }

      // ignore particle matches if full match was found
      if (result.fullDialCodeMatch) continue;

      // particle match with dialCode
      if (phone.length < dialCode.length) {
        if (dialCode.startsWith(phone)) {
          // make sure that we found smallest number dial code
          const isNewCodeLess: boolean = result.country ? Number(dialCode) <= Number(result.country.dialCode) : true;

          if (isNewCodeLess) {
            updateResult({
              country: parsedCountry,
              fullDialCodeMatch: false,
            });
          }
        }
      }
    }

    if (currentCountryIso2) {
      const currentCountry = this.getCountry({
        value: currentCountryIso2,
        field: 'iso2',
        countries,
      });

      if (!currentCountry) {
        return result;
      }

      const getAreaCodesPartialMatch = (country: ParsedCountry) => {
        if (!country.areaCodes) return false;

        const phoneWithoutDialCode = phone.substring(country.dialCode.length);

        return country.areaCodes.some((areaCode) => areaCode.startsWith(phoneWithoutDialCode));
      };

      const shouldSaveCurrentCountry: boolean =
        // countries have same dial code
        result.country?.dialCode === currentCountry.dialCode &&
        result.country !== currentCountry &&
        result.fullDialCodeMatch &&
        // current country area-code is still partially match input
        (!currentCountry.areaCodes || getAreaCodesPartialMatch(currentCountry));

      if (shouldSaveCurrentCountry) {
        result = {
          country: currentCountry,
          fullDialCodeMatch: true,
        };
      }
    }

    return result;
  }
  private applyMask(applyMaskProps: ApplyMaskProps): string {
    const { value, mask, maskSymbol, offset = 0, trimNonMaskCharsLeftover = false } = applyMaskProps;

    if (value.length < offset) return value;

    const savedValuePart: string = value.slice(0, offset);
    const valueToMask: string = value.slice(offset);

    let result: string = savedValuePart;

    let charsPlaced: number = 0;

    for (const maskChar of mask.split('')) {
      if (charsPlaced >= valueToMask.length) {
        if (!trimNonMaskCharsLeftover && maskChar !== maskSymbol) {
          result += maskChar;
          continue;
        }
        break;
      }
      if (maskChar === maskSymbol) {
        result += valueToMask[charsPlaced];
        charsPlaced += 1;
      } else {
        result += maskChar;
      }
    }

    return result;
  }
  private formatPhone(phone: string, config: FormatPhoneConfig): string {
    const shouldForceDialCode: boolean | undefined = config.disableDialCodeAndPrefix ? false : config.forceDialCode;

    const shouldInsertDialCodeOnEmpty: boolean | undefined = config.disableDialCodeAndPrefix ? false : config.insertDialCodeOnEmpty;

    let phoneValue: string = phone;

    const handleResult = (result: string): string => {
      if (config.trimNonDigitsEnd) {
        return result.trim();
      }
      return result;
    };

    // Passed empty value
    if (!phoneValue) {
      if ((shouldInsertDialCodeOnEmpty && !phoneValue.length) || shouldForceDialCode) {
        return handleResult(`${config.prefix}${config.dialCode}${config.charAfterDialCode}`);
      }

      return handleResult(phoneValue);
    }

    // Remove non digit chars from provided value
    phoneValue = this.removeNonDigits(phoneValue);

    // Passed only full dial code
    if (phoneValue === config.dialCode && !config.disableDialCodeAndPrefix) {
      return handleResult(`${config.prefix}${config.dialCode}${config.charAfterDialCode}`);
    }

    // Passed only partial dial code
    if (config.dialCode.startsWith(phoneValue) && !config.disableDialCodeAndPrefix) {
      if (shouldForceDialCode) {
        return handleResult(`${config.prefix}${config.dialCode}${config.charAfterDialCode}`);
      }
      return handleResult(`${config.prefix}${phoneValue}`);
    }

    // Passed phone that not started with dial code
    if (!phoneValue.startsWith(config.dialCode) && !config.disableDialCodeAndPrefix) {
      if (shouldForceDialCode) {
        return handleResult(`${config.prefix}${config.dialCode}${config.charAfterDialCode}`);
      }

      if (phoneValue.length < config.dialCode.length) {
        return handleResult(`${config.prefix}${phoneValue}`);
      }
    }

    const slicePhone = () => {
      const mainPartStartIndex = config.dialCode.length;

      const phoneLeftSide = phoneValue.slice(0, mainPartStartIndex);
      const phoneRightSide = phoneValue.slice(mainPartStartIndex);

      return {
        phoneLeftSide,
        phoneRightSide,
      };
    };

    // slice phone to dialCode and rest value
    // "+12345" (us) -> leftSide: "+1"; rightSide: "2345"
    // leftSide: prefix + dialCode + charAfterDialCode
    // rightSide: rest of phone (that should apply mask)
    let { phoneLeftSide, phoneRightSide } = slicePhone();

    // Handle left side of phone
    phoneLeftSide = `${config.prefix}${phoneLeftSide}${config.charAfterDialCode}`;

    // Handle right side of phone
    phoneRightSide = this.applyMask({
      value: phoneRightSide,
      mask: config.mask,
      maskSymbol: config.maskChar,
      trimNonMaskCharsLeftover: config.trimNonDigitsEnd || (config.disableDialCodeAndPrefix && phoneRightSide.length === 0),
    });

    if (config.disableDialCodeAndPrefix) {
      phoneLeftSide = '';
    }

    return handleResult(`${phoneLeftSide}${phoneRightSide}`);
  }
  private constructFieldNotSupportedErrorMessage(field: keyof ParsedCountry) {
    return `Field "${field}" is not supported`;
  }
  private getDeletionType(inputType?: string) {
    const isDeletion: boolean = inputType?.toLocaleLowerCase().includes('delete') ?? false;
    if (!isDeletion) return undefined;

    return inputType?.toLocaleLowerCase().includes('forward') ? 'forward' : 'backward';
  }

  // public methods

  public parseCountry(countryData: CountryData): ParsedCountry {
    const [name, iso2, dialCode, format, priority, areaCodes] = countryData;
    return {
      name,
      iso2,
      dialCode,
      format,
      priority,
      areaCodes,
    };
  }
  public getCountry(getCountryProps: GetCountryProps): ParsedCountry | undefined {
    const { field, value, countries = defaultCountries } = getCountryProps;

    if (['priority'].includes(field)) {
      throw new Error(this.constructFieldNotSupportedErrorMessage(field));
    }
    const country = countries.find((country) => {
      const parsedCountry = this.parseCountry(country);
      return value === parsedCountry[field];
    });

    if (!country) return undefined;
    return this.parseCountry(country);
  }
  public isMacOS(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.navigator.userAgent.toLowerCase().includes('macintosh');
  }
  public isShallowEqualObjects(object1: Record<string, unknown>, object2: Record<string, unknown>): boolean {
    const keys1: string[] = Object.keys(object1);
    const keys2: string[] = Object.keys(object2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (object1[key] !== object2[key]) {
        return false;
      }
    }

    return true;
  }
  public handleUserInput(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, handleUserInputOptions: HandleUserInputOptions): HandleUserInputReturn {
    const {
      country,
      insertDialCodeOnEmpty,
      phoneBeforeInput,

      prefix,
      charAfterDialCode,
      forceDialCode,
      disableDialCodeAndPrefix,
      countryGuessingEnabled,
      defaultMask,
      disableFormatting,
      countries,
    } = handleUserInputOptions;

    const nativeEvent = e.nativeEvent as InputEvent;

    // Possible input types:
    // https://rawgit.com/w3c/input-events/v1/index.html#interface-InputEvent-Attributes
    const inputType: string | undefined = nativeEvent.inputType;

    const deletion = this.getDeletionType(inputType);

    const isInserted: boolean = !!inputType.startsWith('insertFrom');
    const isTyped: boolean = inputType === 'insertText';

    const nativeEventData: string | null | undefined = nativeEvent.data;
    // Last char that user typed on a keyboard
    const lastTypedChar: string | undefined = nativeEventData || undefined;

    const userInput: string = e.target.value;
    const cursorPositionAfterInput: number = e.target.selectionStart ?? 0;

    // ignore history events (history should be handled manually)
    // only possible way to trigger native history event is to press ctrl+z on empty input
    if (inputType.includes('history')) {
      return {
        inputValue: phoneBeforeInput,
        phone: this.toE164(phoneBeforeInput, prefix),
        cursorPosition: phoneBeforeInput.length,
        country,
      };
    }

    // ignore user input if typed non-digit character
    if (
      isTyped &&
      !this.isNumeric(lastTypedChar) &&
      // allow type prefix when input value is empty
      userInput !== prefix
    ) {
      return {
        inputValue: phoneBeforeInput,
        phone: this.toE164(disableDialCodeAndPrefix ? `${country.dialCode}${phoneBeforeInput}` : phoneBeforeInput, prefix),
        cursorPosition: cursorPositionAfterInput - (lastTypedChar?.length ?? 0),
        country,
      };
    }

    // forceDialCode: ignore dial code change (only if prefixed phone was not inserted)
    if (
      forceDialCode &&
      // dial code has been changed
      !userInput.startsWith(`${prefix}${country.dialCode}`) &&
      // was not inserted with ctrl+v
      !isInserted
    ) {
      const inputValue: string = userInput ? phoneBeforeInput : `${prefix}${country.dialCode}${charAfterDialCode}`;

      return {
        inputValue,
        phone: this.toE164(inputValue, prefix),
        cursorPosition: prefix.length + country.dialCode.length + charAfterDialCode.length, // set cursor position after dial code
        country,
      };
    }

    const {
      phone: newPhone,
      inputValue: newInputValue,
      country: newCountry,
    } = this.handlePhoneChange({
      value: userInput,
      country,

      trimNonDigitsEnd: deletion === 'backward', // trim values if user deleting chars (delete mask's whitespace and brackets)
      insertDialCodeOnEmpty,
      countryGuessingEnabled,

      countries,
      prefix,
      charAfterDialCode,
      forceDialCode,
      disableDialCodeAndPrefix,
      disableFormatting,
      defaultMask,
    });

    const newCursorPosition: number = this.getCursorPosition({
      cursorPositionAfterInput,
      phoneBeforeInput,
      phoneAfterInput: userInput,
      phoneAfterFormatted: newInputValue,
      leftOffset: forceDialCode ? prefix.length + country.dialCode.length + charAfterDialCode.length : 0,
      deletion,
    });

    return {
      phone: newPhone,
      inputValue: newInputValue,
      cursorPosition: newCursorPosition,
      country: newCountry,
    };
  }
  public handlePhoneChange(handlePhoneChangeProps: HandlePhoneChangeProps): HandlePhoneChangeReturn {
    const {
      value,
      country,
      insertDialCodeOnEmpty,
      trimNonDigitsEnd,

      countries,
      prefix,
      charAfterDialCode,
      forceDialCode,
      disableDialCodeAndPrefix,
      defaultMask,
      countryGuessingEnabled,
      disableFormatting,
    } = handlePhoneChangeProps;
    let inputPhone: string = value;

    // make sure that inputPhone starts with dial code when dial code is disabled
    if (disableDialCodeAndPrefix) {
      inputPhone = inputPhone.startsWith(`${prefix}`) ? inputPhone : `${prefix}${country.dialCode}${inputPhone}`;
    }

    const countryGuessResult = countryGuessingEnabled
      ? this.guessCountryByPartialNumber({
          phone: inputPhone,
          countries,
          currentCountryIso2: country.iso2,
        }) // FIXME: should not guess country on every change
      : undefined;

    const formatCountry = countryGuessResult?.country ?? country;

    const inputValue: string = this.formatPhone(inputPhone, {
      prefix,
      mask: this.getActiveFormattingMask({
        phone: inputPhone,
        country: formatCountry,
        defaultMask,
        disableFormatting,
      }),
      maskChar: this.MASK_CHAR,
      dialCode: formatCountry.dialCode,
      trimNonDigitsEnd,
      charAfterDialCode,
      forceDialCode,
      insertDialCodeOnEmpty,
      disableDialCodeAndPrefix,
    });

    const resultCountry = countryGuessingEnabled && !countryGuessResult?.fullDialCodeMatch ? country : formatCountry;

    const phone: string = this.toE164(disableDialCodeAndPrefix ? `${resultCountry.dialCode}${inputValue}` : inputValue, prefix);

    return {
      phone,
      inputValue,
      country: resultCountry,
    };
  }
}
